import React, { useEffect, useState, useRef } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { supabase, getCurrentUser } from '../lib/supabase';
import { IconBell } from './Icons';
import { useCheckInDue } from './OnboardingChecklist';

// Primary tabs are always visible (tablet + desktop).
const PRIMARY_TABS = [
  { to: '/dashboard', label: 'Overview' },
  { to: '/sleep',     label: 'Sleep' },
  { to: '/movement',  label: 'Movement' },
  { to: '/nutrition', label: 'Nutrition' },
  { to: '/stress',    label: 'Stress' },
];

// Secondary tabs render inline only at xl+ (≥1280px). At md/lg they fold
// into an inline "More" dropdown to keep the tablet header uncluttered.
const SECONDARY_TABS = [
  { to: '/social-substances', label: 'Social & Substances' },
  { to: '/phenotype',         label: 'Drivers & Patterns' },
  { to: '/checkin',           label: 'Check-In' },
];

interface TopNavProps {
  isAdmin?: boolean;
  isClinician?: boolean;
}

export default function TopNav({ isAdmin, isClinician }: TopNavProps) {
  const checkInDue = useCheckInDue();
  const [unreadCount, setUnreadCount] = useState(0);
  const [profileOpen, setProfileOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Close dropdowns on route change
  useEffect(() => {
    setProfileOpen(false);
    setMoreOpen(false);
  }, [location.pathname]);

  // Close on outside click or Escape key — shared handler for both dropdowns
  useEffect(() => {
    if (!profileOpen && !moreOpen) return;
    function handleClick(e: MouseEvent) {
      if (profileOpen && profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
      if (moreOpen && moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setProfileOpen(false);
        setMoreOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [profileOpen, moreOpen]);

  // Fetch unread alert count for clinicians
  useEffect(() => {
    if (!isClinician && !isAdmin) return;

    async function fetchAlertCount() {
      const user = await getCurrentUser();
      if (!user) return;

      const { count } = await supabase
        .from('alerts')
        .select('id', { count: 'exact', head: true })
        .eq('clinician_id', user.id)
        .eq('read', false);

      setUnreadCount(count || 0);
    }

    fetchAlertCount();
    const interval = setInterval(fetchAlertCount, 60000);
    return () => clearInterval(interval);
  }, [isClinician, isAdmin]);

  async function handleSignOut() {
    await supabase.auth.signOut();
    navigate('/login');
  }

  // Profile menu items
  const profileItems = [
    { to: '/action-plan', label: 'Action Plan' },
    { to: '/labs',        label: 'All Labs' },
    { to: '/connect',     label: 'Data Sources' },
    { to: '/history',     label: 'History' },
    { to: '/settings',    label: 'Settings' },
    ...(isClinician || isAdmin ? [{ to: '/patients', label: 'Patients' }] : []),
    ...(isAdmin ? [{ to: '/admin', label: 'Admin' }] : []),
  ];

  const profileRouteActive = profileItems.some(t => location.pathname === t.to || location.pathname.startsWith(t.to + '/'));

  // Reusable tab renderer so primary + secondary (inline at xl) share styling
  const renderTab = ({ to, label }: { to: string; label: string }) => (
    <NavLink
      key={to}
      to={to}
      className="px-3.5 py-3 text-[13px] border-b-2 whitespace-nowrap"
      style={({ isActive }) => ({
        color: isActive ? '#1B3022' : 'rgba(27,48,34,0.68)',
        letterSpacing: '0.04em',
        fontWeight: isActive ? 600 : 450,
        borderBottomColor: isActive ? '#6F8A5A' : 'transparent',
        transition: 'all 0.3s ease',
      })}
      onMouseEnter={(e) => {
        const isAct = e.currentTarget.getAttribute('aria-current') === 'page';
        if (!isAct) {
          e.currentTarget.style.color = 'rgba(27,48,34,0.88)';
          e.currentTarget.style.borderBottomColor = 'rgba(111,138,90,0.45)';
        }
      }}
      onMouseLeave={(e) => {
        const isAct = e.currentTarget.getAttribute('aria-current') === 'page';
        if (!isAct) {
          e.currentTarget.style.color = 'rgba(27,48,34,0.68)';
          e.currentTarget.style.borderBottomColor = 'transparent';
        }
      }}
    >
      <span className="flex items-center gap-1.5">
        {label}
        {label === 'Check-In' && checkInDue && (
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse flex-shrink-0" />
        )}
      </span>
    </NavLink>
  );

  // Is any SECONDARY_TABS route currently active? Used to highlight "More"
  const moreRouteActive = SECONDARY_TABS.some(
    (t) => location.pathname === t.to || location.pathname.startsWith(t.to + '/'),
  );

  return (
    <nav
      role="navigation"
      aria-label="Main navigation"
      className="hidden md:flex items-center gap-1 px-5 sticky top-0 z-30"
      style={{
        background: 'rgba(243,247,240,0.97)',
        backdropFilter: 'blur(20px) saturate(1.1)',
        WebkitBackdropFilter: 'blur(20px) saturate(1.1)',
        borderBottom: '1px solid rgba(214,224,207,0.45)',
      }}
    >
      {/* Brand name — serif, left side */}
      <Link
        to="/dashboard"
        className="mr-4 pr-4 py-3 flex-shrink-0"
        style={{
          fontFamily: "'Cormorant Garamond', 'DM Serif Display', Georgia, serif",
          fontSize: '1.1rem',
          fontWeight: 600,
          color: '#1B3022',
          letterSpacing: '0.02em',
          borderRight: '1px solid rgba(47,74,52,0.18)',
          transition: 'opacity 0.3s ease',
        }}
      >
        Arete Integrated Health
      </Link>

      {/* Primary tabs — always visible at md+ */}
      {PRIMARY_TABS.map(renderTab)}

      {/* Secondary tabs — inline at xl+, folded into More dropdown at md/lg */}
      <div className="hidden xl:contents">
        {SECONDARY_TABS.map(renderTab)}
      </div>
      <div className="xl:hidden relative" ref={moreRef}>
        <button
          type="button"
          onClick={() => setMoreOpen((v) => !v)}
          aria-haspopup="true"
          aria-expanded={moreOpen}
          aria-label="More navigation options"
          className="px-3.5 py-3 text-[13px] border-b-2 whitespace-nowrap flex items-center gap-1"
          style={{
            color: moreRouteActive || moreOpen ? '#1B3022' : 'rgba(27,48,34,0.68)',
            letterSpacing: '0.04em',
            fontWeight: moreRouteActive ? 600 : 450,
            borderBottomColor: moreRouteActive ? '#6F8A5A' : 'transparent',
            transition: 'all 0.3s ease',
            background: 'transparent',
          }}
        >
          More
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M3 4.5L6 7.5L9 4.5" />
          </svg>
          {checkInDue && (
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse flex-shrink-0 ml-0.5" />
          )}
        </button>
        {moreOpen && (
          <div
            role="menu"
            aria-label="More navigation"
            className="absolute top-full left-0 mt-1 py-1 rounded-xl shadow-lg min-w-[200px] z-50"
            style={{
              background: 'rgba(248,250,246,0.98)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(214,224,207,0.55)',
            }}
          >
            {SECONDARY_TABS.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                role="menuitem"
                className="block px-4 py-2 text-[13px] transition-colors"
                style={({ isActive }) => ({
                  color: isActive ? '#1B3022' : 'rgba(27,48,34,0.72)',
                  fontWeight: isActive ? 600 : 400,
                  background: isActive ? 'rgba(111,138,90,0.08)' : 'transparent',
                })}
              >
                <span className="flex items-center gap-1.5">
                  {label}
                  {label === 'Check-In' && checkInDue && (
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse flex-shrink-0" />
                  )}
                </span>
              </NavLink>
            ))}
          </div>
        )}
      </div>

      {/* Right side — bell + profile monogram with dropdown */}
      <div className="ml-auto flex items-center gap-2">
        {(isClinician || isAdmin) && (
          <Link
            to="/patients"
            className="relative p-3 rounded-lg"
            style={{ background: 'transparent', transition: 'background 0.3s ease' }}
            aria-label={unreadCount > 0 ? `${unreadCount} unread alert${unreadCount !== 1 ? 's' : ''}` : 'Alerts'}
            title={unreadCount > 0 ? `${unreadCount} unread alert${unreadCount !== 1 ? 's' : ''}` : 'No alerts'}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(27,48,34,0.06)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            <IconBell size={18} color={unreadCount > 0 ? '#EF4444' : 'rgba(27,48,34,0.35)'} />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </Link>
        )}

        {/* Profile monogram — opens dropdown */}
        <div className="relative" ref={profileRef}>
          <button
            onClick={() => setProfileOpen(o => !o)}
            aria-label="Profile menu"
            aria-expanded={profileOpen}
            aria-haspopup="true"
            className="flex items-center justify-center rounded-full flex-shrink-0"
            style={{
              width: 44,
              height: 44,
              background: profileRouteActive || profileOpen ? 'rgba(47,74,52,0.14)' : 'rgba(47,74,52,0.08)',
              border: `1px solid ${profileRouteActive ? 'rgba(111,138,90,0.4)' : 'rgba(47,74,52,0.10)'}`,
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontSize: '1.05rem',
              fontWeight: 600,
              color: '#2F4A34',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(47,74,52,0.14)'; }}
            onMouseLeave={e => {
              if (!profileOpen && !profileRouteActive) e.currentTarget.style.background = 'rgba(47,74,52,0.08)';
            }}
          >
            A
          </button>
          {profileOpen && (
            <div
              role="menu"
              aria-label="Profile options"
              className="absolute top-full right-0 mt-1 py-1 rounded-xl shadow-lg min-w-[160px] z-50"
              style={{
                background: 'rgba(248,250,246,0.98)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(214,224,207,0.55)',
              }}
            >
              {profileItems.map(({ to, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  className="block px-4 py-2 text-[13px] transition-colors"
                  style={({ isActive }) => ({
                    color: isActive ? '#1B3022' : 'rgba(27,48,34,0.72)',
                    fontWeight: isActive ? 600 : 400,
                    background: isActive ? 'rgba(111,138,90,0.08)' : 'transparent',
                  })}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(111,138,90,0.10)'; }}
                  onMouseLeave={e => {
                    const isAct = e.currentTarget.getAttribute('aria-current') === 'page';
                    e.currentTarget.style.background = isAct ? 'rgba(111,138,90,0.08)' : 'transparent';
                  }}
                >
                  {label}
                </NavLink>
              ))}
              <div className="border-t my-1" style={{ borderColor: 'rgba(214,224,207,0.45)' }} />
              <button
                onClick={handleSignOut}
                className="block w-full text-left px-4 py-2 text-[13px] transition-colors"
                style={{ color: 'rgba(27,48,34,0.60)' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(111,138,90,0.10)'; e.currentTarget.style.color = '#1B3022'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(27,48,34,0.60)'; }}
              >
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
